import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { useAuth } from '../../contexts/AuthContext';
import { BellIcon, EnvelopeIcon, ComputerDesktopIcon, GlobeAltIcon, EnvelopeOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface UserPreferences {
  id: string;
  user_id: string;
  theme: string;
  language: string;
  timezone: string;
  email_notifications: boolean;
  desktop_notifications: boolean;
  web_notifications: boolean;
  send_invite_to_unregistered_recipients?: boolean;
  require_2fa: boolean;
  backup_master_keys: boolean;
  allow_multi_device_access: boolean;
  session_timeout_value: number;
  session_timeout_unit: 'minutes' | 'hours';
  quick_encrypt_policy: string;
  created_at: string;
  updated_at: string;
}

const NotificationsTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { preferences: userPreferences, loading, refreshPreferences } = useUserPreferences();
  const isPersonalUser = !user?.company_id;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (field: keyof UserPreferences, value: boolean) => {
    if (!userPreferences || saving) return;

    try {
      setSaving(true);
      setError(null);

      await apiService.updateUserPreferences({
        [field]: value,
      });

      // Refresh preferences from context to update all components
      await refreshPreferences();
      toast.success(t('notifications.updated'));
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      setError(t('notifications.updateFailed'));
      toast.error(t('notifications.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <div className="px-4 py-5 sm:px-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userPreferences) {
    return (
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <div className="px-4 py-5 sm:px-6">
          <div className="text-red-600 dark:text-red-400">{error}</div>
          <button
            onClick={() => refreshPreferences()}
            className="mt-4 px-4 py-2 bg-governate-600 text-white rounded-md hover:bg-governate-700"
          >
            {t('notifications.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          <BellIcon className="h-5 w-5 mr-2" />
          {t('notifications.title')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          {t('notifications.description')}
        </p>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-800">
        <div className="px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-800">
            {/* Web Notifications */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <GlobeAltIcon className="h-5 w-5 mr-2" />
                {t('notifications.webNotifications')}
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('notifications.webNotificationsDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle('web_notifications', !userPreferences?.web_notifications)}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                      userPreferences?.web_notifications
                        ? 'bg-governate-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={userPreferences?.web_notifications}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userPreferences?.web_notifications ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </dd>
            </div>

            {/* Email Notifications */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                {t('notifications.emailNotifications')}
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('notifications.emailNotificationsDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle('email_notifications', !userPreferences?.email_notifications)}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                      userPreferences?.email_notifications
                        ? 'bg-governate-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={userPreferences?.email_notifications}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userPreferences?.email_notifications ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </dd>
            </div>

            {/* Desktop Notifications */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <ComputerDesktopIcon className="h-5 w-5 mr-2" />
                {t('notifications.desktopNotifications')}
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('notifications.desktopNotificationsDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle('desktop_notifications', !userPreferences?.desktop_notifications)}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                      userPreferences?.desktop_notifications
                        ? 'bg-governate-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={userPreferences?.desktop_notifications}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userPreferences?.desktop_notifications ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </dd>
            </div>

            {/* Send invitation email to unregistered recipients (personal users only) */}
            {isPersonalUser && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                  <EnvelopeOpenIcon className="h-5 w-5 mr-2" />
                  {t('notifications.sendInviteToUnregistered')}
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('notifications.sendInviteToUnregisteredDescription')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggle('send_invite_to_unregistered_recipients', !(userPreferences?.send_invite_to_unregistered_recipients ?? true))}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                        (userPreferences?.send_invite_to_unregistered_recipients ?? true)
                          ? 'bg-governate-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={userPreferences?.send_invite_to_unregistered_recipients ?? true}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (userPreferences?.send_invite_to_unregistered_recipients ?? true) ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;

